import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Press and hold to continue.
 *
 * This exists for exactly one moment: handing the phone to the next player in
 * pass-and-play. A plain button there is a disaster — the phone is mid-air, a
 * thumb brushes the glass, and somebody's hand is on screen in front of the
 * whole table with no way to un-see it.
 *
 * The accessibility escape hatch is not optional. A hold is a hostile gesture
 * for anyone with a tremor or using a switch device, so after two abandoned
 * attempts a plain button appears alongside it. Two, rather than one, because a
 * single mis-tap is common and shouldn't immediately downgrade the safeguard.
 */
const HOLD_MS = 420;

export function HoldButton({
  label,
  onComplete,
  className = 'btn',
}: {
  label: string;
  onComplete: () => void;
  className?: string;
}) {
  const [progress, setProgress] = useState(0);
  const [abandoned, setAbandoned] = useState(0);
  const raf = useRef(0);
  const startedAt = useRef(0);
  const held = useRef(false);
  const doneRef = useRef(onComplete);
  doneRef.current = onComplete;

  const stop = useCallback(() => {
    cancelAnimationFrame(raf.current);
    raf.current = 0;
    if (held.current && progress < 1) setAbandoned((n) => n + 1);
    held.current = false;
    setProgress(0);
  }, [progress]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const begin = useCallback((ev: React.PointerEvent<HTMLButtonElement>) => {
    // Without capture, sliding a thumb one pixel off the button cancels the hold
    // and the player is told nothing about why.
    try {
      ev.currentTarget.setPointerCapture(ev.pointerId);
    } catch {
      /* not all pointer types support capture */
    }
    ev.preventDefault();
    held.current = true;
    startedAt.current = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - startedAt.current) / HOLD_MS);
      setProgress(k);
      if (k >= 1) {
        held.current = false;
        raf.current = 0;
        doneRef.current();
        return;
      }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, []);

  return (
    <>
      <button
        type="button"
        className={`${className} hold${progress > 0 ? ' hold--active' : ''}`}
        onPointerDown={begin}
        onPointerUp={stop}
        onPointerCancel={stop}
        onPointerLeave={stop}
        // Keyboard and switch users get a plain activation: a hold is not
        // expressible with Enter, and requiring one would lock them out.
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            doneRef.current();
          }
        }}
      >
        {/*
          The fill is driven by a CSS animation keyed off `.hold--active`, not by
          a width this component writes. There is no `style-src-attr
          'unsafe-inline'` in the CSP, so an inline width or a custom property
          set from JS would be dropped on the floor and the bar would never move.
          Both sides are HOLD_MS, so they stay in step.
        */}
        <span className="hold__fill" />
        <span className="hold__label">{label}</span>
      </button>
      {abandoned >= 2 ? (
        <button type="button" className="btn ghost" onClick={() => doneRef.current()}>
          Hold not working? Tap here instead
        </button>
      ) : null}
    </>
  );
}
